/**
 * Sortable table component with dark theme styling.
 */
import { useState, useCallback, useMemo, KeyboardEvent, MouseEvent, Fragment } from 'react';
import { clsx } from 'clsx';

export type SortDirection = 'asc' | 'desc' | null;

export interface Column<T> {
  key: string;
  header: string;
  sortable?: boolean;
  render?: (item: T) => React.ReactNode;
  className?: string;
  align?: 'left' | 'center' | 'right';
}

interface TableProps<T> {
  data: T[];
  columns: Column<T>[];
  keyExtractor: (item: T) => string;
  onSort?: (key: string, direction: SortDirection) => void;
  sortKey?: string;
  sortDirection?: SortDirection;
  emptyMessage?: string;
  className?: string;
  /** Optional render function for expanded content below a row */
  renderExpandedContent?: (item: T) => React.ReactNode | null;
  /** Optional click handler for row body */
  onRowClick?: (item: T) => void;
  /** Optional extra column prepended before existing columns */
  prependColumn?: Column<T>;
}

const alignClasses = {
  left: 'text-left',
  center: 'text-center',
  right: 'text-right',
};

export function Table<T extends object>({
  data,
  columns,
  keyExtractor,
  onSort,
  sortKey,
  sortDirection,
  emptyMessage = 'No data available',
  className,
  renderExpandedContent,
  onRowClick,
  prependColumn,
}: TableProps<T>) {
  const [internalSortKey, setInternalSortKey] = useState<string | null>(null);
  const [internalSortDirection, setInternalSortDirection] = useState<SortDirection>(null);

  const activeSortKey = sortKey ?? internalSortKey;
  const activeSortDirection = sortDirection ?? internalSortDirection;

  const handleSort = useCallback(
    (key: string) => {
      let newDirection: SortDirection;

      if (activeSortKey === key) {
        if (activeSortDirection === 'asc') {
          newDirection = 'desc';
        } else if (activeSortDirection === 'desc') {
          newDirection = null;
        } else {
          newDirection = 'asc';
        }
      } else {
        newDirection = 'asc';
      }

      if (onSort) {
        onSort(key, newDirection);
      } else {
        setInternalSortKey(newDirection ? key : null);
        setInternalSortDirection(newDirection);
      }
    },
    [activeSortKey, activeSortDirection, onSort]
  );

  const handleKeyDown = useCallback(
    (key: string) => (e: KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        handleSort(key);
      }
    },
    [handleSort]
  );

  const allColumns = useMemo(() => {
    return prependColumn ? [prependColumn, ...columns] : columns;
  }, [prependColumn, columns]);

  const handleRowClick = useCallback(
    (item: T) => (e: MouseEvent<HTMLTableRowElement>) => {
      if (!onRowClick) return;
      const target = e.target as HTMLElement;
      if (target.closest('button, a, input, select, textarea')) return;
      onRowClick(item);
    },
    [onRowClick]
  );

  const sortedData = useMemo(() => {
    if (!activeSortKey || !activeSortDirection || onSort) {
      return data;
    }

    return [...data].sort((a, b) => {
      const aVal = (a as Record<string, unknown>)[activeSortKey];
      const bVal = (b as Record<string, unknown>)[activeSortKey];

      if (aVal === bVal) return 0;
      if (aVal === null || aVal === undefined) return 1;
      if (bVal === null || bVal === undefined) return -1;

      const comparison = aVal < bVal ? -1 : 1;
      return activeSortDirection === 'asc' ? comparison : -comparison;
    });
  }, [data, activeSortKey, activeSortDirection, onSort]);

  const getSortIcon = (key: string) => {
    if (activeSortKey !== key) {
      return (
        <span className="ml-1 text-dark-600" aria-hidden="true">
          ↕
        </span>
      );
    }
    return (
      <span className="ml-1 text-primary-400" aria-hidden="true">
        {activeSortDirection === 'asc' ? '↑' : '↓'}
      </span>
    );
  };

  const getAriaSort = (key: string): 'ascending' | 'descending' | 'none' => {
    if (activeSortKey !== key) return 'none';
    return activeSortDirection === 'asc' ? 'ascending' : 'descending';
  };

  return (
    <div className={clsx('overflow-x-auto scrollbar-thin', className)}>
      <table className="min-w-full divide-y divide-dark-700">
        <thead>
          <tr>
            {allColumns.map((column) => (
              <th
                key={column.key}
                scope="col"
                className={clsx(
                  'px-4 py-3 text-xs font-semibold uppercase tracking-wider text-dark-400',
                  alignClasses[column.align || 'left'],
                  column.sortable && 'cursor-pointer select-none hover:text-dark-200 transition-colors',
                  column.className
                )}
                aria-sort={column.sortable ? getAriaSort(column.key) : undefined}
                tabIndex={column.sortable ? 0 : undefined}
                onClick={column.sortable ? () => handleSort(column.key) : undefined}
                onKeyDown={column.sortable ? handleKeyDown(column.key) : undefined}
                role={column.sortable ? 'columnheader button' : 'columnheader'}
              >
                <span className="inline-flex items-center">
                  {column.header}
                  {column.sortable && getSortIcon(column.key)}
                </span>
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-dark-800">
          {sortedData.length === 0 ? (
            <tr>
              <td
                colSpan={allColumns.length}
                className="px-4 py-8 text-center text-sm text-dark-500"
              >
                {emptyMessage}
              </td>
            </tr>
          ) : (
            sortedData.map((item) => {
              const expandedContent = renderExpandedContent ? renderExpandedContent(item) : null;
              return (
                <Fragment key={keyExtractor(item)}>
                  <tr
                    className={clsx(
                      'hover:bg-dark-800/50 transition-colors',
                      onRowClick && 'cursor-pointer'
                    )}
                    onClick={onRowClick ? handleRowClick(item) : undefined}
                  >
                    {allColumns.map((column) => (
                      <td
                        key={column.key}
                        className={clsx(
                          'whitespace-nowrap px-4 py-3 text-sm text-dark-200',
                          alignClasses[column.align || 'left'],
                          column.className
                        )}
                      >
                        {column.render
                          ? column.render(item)
                          : String((item as Record<string, unknown>)[column.key] ?? '')}
                      </td>
                    ))}
                  </tr>
                  {expandedContent !== null && (
                    <tr className="expanded-content-row">
                      <td colSpan={allColumns.length} className="p-0">
                        <div
                          className="overflow-hidden transition-[max-height] duration-200 ease-in-out"
                          style={{ maxHeight: '1000px' }}
                        >
                          {expandedContent}
                        </div>
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })
          )}
        </tbody>
      </table>
    </div>
  );
}
